import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';

import { requestDreamReading, type ReadingRecord } from '@/api/client';
import OrnateFrame from '@/components/OrnateFrame';
import RichText from '@/components/RichText';
import TarotCard from '@/components/TarotCard';
import { useAudio } from '@/hooks/useAudio';
import { usePayment } from '@/hooks/usePayment';
import { useSubscription } from '@/hooks/useSubscription';

/**
 * Dream reading screen.
 *
 * User types in their dream → pays 25⭐ (free for Priestess) → backend
 * pulls a card and AI weaves a 3-paragraph interpretation: dream
 * symbolism + how the card colours it + practical cue for today.
 */
export default function Dream() {
  const { t } = useTranslation();
  const [dream, setDream] = useState('');
  const [reading, setReading] = useState<ReadingRecord | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { pay, paying, payError } = usePayment('dream_reading');
  const { isPriestess } = useSubscription();
  const audio = useAudio();

  const charCount = dream.trim().length;
  const canSubmit = charCount >= 20 && charCount <= 1000;

  const runReading = async () => {
    setError(null);
    try {
      const r = await requestDreamReading({ dream_text: dream });
      audio.play('shuffle');
      setReading(r);
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const onSubmit = () => {
    if (!canSubmit || busy) return;
    if (isPriestess) {
      setBusy(true);
      runReading().finally(() => setBusy(false));
      return;
    }
    pay(runReading);
  };

  const reset = () => {
    setReading(null);
    setDream('');
    setError(null);
  };

  return (
    <main className="mx-auto max-w-md px-5 pb-24 pt-2">
      <h1 className="mb-1 text-center font-serif text-2xl text-gold-300">
        ☾ {t('dream.title')}
      </h1>
      <p className="mb-6 text-center text-xs italic text-gold-300/60">
        {t('dream.subtitle')}
      </p>

      <AnimatePresence mode="wait">
        {!reading ? (
          <motion.section
            key="form"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
          >
            <OrnateFrame className="card-glow mb-4">
              <label className="block">
                <span className="mb-2 block text-[10px] uppercase tracking-widest text-gold-500/60">
                  {t('dream.input_label')}
                </span>
                <textarea
                  value={dream}
                  onChange={(e) => setDream(e.target.value)}
                  placeholder={t('dream.placeholder')}
                  rows={7}
                  maxLength={1000}
                  className="w-full resize-none rounded-lg border border-gold-500/30
                             bg-night-900/60 px-3 py-2 font-serif text-base
                             text-gold-100 outline-none transition
                             placeholder:text-gold-300/35 focus:border-gold-400"
                />
                <div className="mt-1 flex items-center justify-between text-[10px] text-gold-300/40">
                  <span>{t('dream.min_chars_hint')}</span>
                  <span>{charCount}/1000</span>
                </div>
              </label>
            </OrnateFrame>

            <button
              type="button"
              onClick={onSubmit}
              disabled={!canSubmit || paying || busy}
              className="btn-primary w-full disabled:opacity-40"
            >
              {paying || busy
                ? t('payment.opening')
                : isPriestess
                  ? t('dream.read_priestess')
                  : t('dream.read_for_stars', { stars: 25 })}
            </button>
            {isPriestess && (
              <p className="mt-2 text-center text-[10px] uppercase tracking-widest text-gold-400/70">
                ✦ {t('spread.included_priestess')}
              </p>
            )}
            {(payError || error) && (
              <p className="mt-3 text-center text-xs text-rose-400">
                {payError ?? error}
              </p>
            )}
            <p className="mt-4 text-center text-xs italic text-gold-300/50">
              {t('dream.description')}
            </p>
          </motion.section>
        ) : (
          <motion.section
            key="result"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-4"
          >
            <div className="flex justify-center">
              <motion.div
                initial={{ rotateY: 180, opacity: 0 }}
                animate={{ rotateY: 0, opacity: 1 }}
                transition={{ duration: 0.9, ease: 'easeOut' }}
              >
                <TarotCard card={reading.cards[0]} size={180} />
              </motion.div>
            </div>

            <OrnateFrame className="card-glow">
              <RichText
                text={reading.interpretation}
                className="font-serif italic leading-relaxed text-gold-200"
              />
            </OrnateFrame>

            <div className="mt-4 flex items-center justify-between gap-3">
              <button type="button" onClick={reset} className="nav-back-pill">
                <span>←</span>
                <span>{t('dream.new_dream')}</span>
              </button>
              <button
                type="button"
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                aria-label={t('reading.scroll_to_top')}
                className="nav-scroll-top"
              >
                ↑
              </button>
            </div>
          </motion.section>
        )}
      </AnimatePresence>
    </main>
  );
}
